/**
 * Preloads Pearl product iframes inside the inline panel body.
 *
 * Creates one hidden iframe per URL-bearing action. Iframes stay mounted in
 * `.pw-pnl-body` — show/hide via `display`, never reparent (reparenting resets
 * the browsing context).
 */

import type { PearlAction } from './icon-config';
import { VOICE_ACTION } from './icon-config';
import { buildUrl, PI_BASE, type BuildUrlContext } from './url-builder';
import { createLogger } from '../ext-core/logger';
import { INLINE_PANEL_SANDBOX, VOICE_PANEL_SANDBOX } from './inline-panel';
import {
  PRELOAD_ATTR,
  PENDING_SRC_ATTR,
  PRELOAD_LOADED_ATTR,
  LOAD_ON_OPEN_ATTR,
} from './preload-attrs';
import {
  getIframeOrigin,
  isIframeReady,
  sendAuthToIframe,
} from '../voice-iframe/internal/protocol-bridge';

function normalizeUrl(url: string): string {
  try {
    return new URL(url).href;
  } catch {
    return url;
  }
}

function urlsEqual(a: string, b: string): boolean {
  return normalizeUrl(a) === normalizeUrl(b);
}

/**
 * Practice Intelligence ignores SCRIBE_EMBEDDED and runs its own Auth0 login.
 * A hidden iframe can't finish that login when the account needs MFA, so an
 * eager preload starts an unanswerable MFA challenge on every page load.
 *
 * @param url - Resolved iframe URL
 * @returns True when the iframe must wait for the panel to open it
 */
function needsOwnLogin(url: string): boolean {
  try {
    return new URL(url).origin === new URL(PI_BASE).origin;
  } catch {
    return false;
  }
}

const toothLogger = createLogger('pearl-shell', 'content');

/**
 * Records the exact Second Opinion URL the tooth iframe is about to load.
 *
 * @param url - Resolved tooth iframe URL, including the patient id
 * @param context - Patient context used to build that URL
 */
function logToothNavigate(url: string, context: BuildUrlContext): void {
  toothLogger.info('tooth_navigate', {
    action: 'tooth',
    has_patient: !!context.patientId,
    url_kind: url.includes('/#/disambiguation/') ? 'disambiguation' : 'home',
    url_path: url,
  });
}

export interface GradualPreloadOptions {
  /** Initial settle delay before Second Opinion (`tooth`) starts preloading. Default 2000ms. */
  initialDelayMs?: number;
  /** Quiet interval after one iframe finishes loading before starting the next. Default 3000ms. */
  stepDelayMs?: number;
  /** Max time to wait for an iframe's `load` event before advancing the queue. Default 5000ms. */
  loadTimeoutMs?: number;
  /** Priority order for gradual preloading. Defaults to `tooth` first, then non-PI actions. */
  priorityOrder?: readonly PearlAction[];
}

const DEFAULT_GRADUAL_INITIAL_DELAY_MS = 2000;
const DEFAULT_GRADUAL_STEP_DELAY_MS = 3000;
const DEFAULT_GRADUAL_LOAD_TIMEOUT_MS = 5000;
const DEFAULT_GRADUAL_IDLE_FALLBACK_MS = 50;

const DEFAULT_GRADUAL_PRIORITY: readonly PearlAction[] = [
  'tooth',
  'patient',
  'treatments',
  'insurance',
  'calendar',
  'dashboard',
  'production',
  'funnels',
  'canceled',
  'call-sheets',
];

function isDocVisible(doc: Document): boolean {
  return doc.visibilityState !== 'hidden';
}

/**
 * Optional overrides for {@link createIframePreloader}.
 */
export interface IframePreloaderDeps {
  /**
   * Custom URL resolver (defaults to `buildUrl` from `url-builder.ts`).
   * The demo harness passes the sandbox `buildUrl` implementation here.
   */
  buildUrlImpl?: (
    action: PearlAction,
    context: BuildUrlContext,
  ) => string | null;
  /**
   * When true, every iframe (not only Practice Intelligence ones) is created
   * without navigating until the panel claims it via `claimForOpen`. Use on
   * hosts where eager SO boots spam the parent console with mismatched
   * `postMessage` target origins.
   */
  deferNavigation?: boolean;
  /**
   * When enabled, non-PI iframes (`tooth` first, then Precheck `insurance`, etc.)
   * are mounted with `data-pending-src` at t=0 and navigated one-by-one after
   * the host page settles, only while `document.visibilityState === 'visible'` (DOCK-140).
   */
  gradualNavigation?: boolean | GradualPreloadOptions;
}

export interface IframePreloader {
  /**
   * Creates hidden iframes for each action that resolves to a URL (skips `voice`).
   * Replaces any previously preloaded pool tied to this handle.
   */
  create: (actions: readonly PearlAction[], context: BuildUrlContext) => void;
  /** Returns the preload iframe for an action, or null if none. */
  getIframe: (action: PearlAction) => HTMLIFrameElement | null;
  /**
   * Resolves the preload iframe for a URL the panel is about to show, and
   * navigates it first when its navigation was deferred. This is the only
   * lookup used to open a panel — a plain match would hand back a blank iframe
   * on deferred hosts (DOCK-9).
   */
  claimForOpen: (url: string) => HTMLIFrameElement | null;
  /**
   * Recomputes URLs from `buildUrl` for the current action set; updates `src`
   * only when the URL changed.
   */
  refresh: (context: BuildUrlContext) => void;
  /**
   * Recomputes and (if changed) reloads a single action's iframe. Use this
   * instead of `refresh` when only one product page needs updating (e.g.
   * Second Opinion after a patient change).
   */
  refreshAction: (action: PearlAction, context: BuildUrlContext) => void;
  /** Removes all preload iframes and resets internal state. */
  destroy: () => void;
  /**
   * Sends SCRIBE_EMBEDDED auth payload to every preloaded iframe via postMessage.
   * Skips iframes that have not finished navigating to their product origin;
   * retries automatically on each iframe `load`.
   */
  sendAuthToAll: (payload: Record<string, unknown>) => void;
}

/**
 * @param doc - Document used to create elements.
 * @param panelBody - The `.pw-pnl-body` element (preload iframes append here).
 * @param deps - Optional overrides (e.g. sandbox `buildUrl` for the demo harness).
 */
export function createIframePreloader(
  doc: Document,
  panelBody: HTMLElement,
  deps?: IframePreloaderDeps,
): IframePreloader {
  const resolveUrl = deps?.buildUrlImpl ?? buildUrl;
  const deferNavigation = !!deps?.deferNavigation;
  const gradualOpts: GradualPreloadOptions | null =
    !deferNavigation && deps?.gradualNavigation
      ? typeof deps.gradualNavigation === 'object'
        ? deps.gradualNavigation
        : {}
      : null;
  const byAction = new Map<PearlAction, HTMLIFrameElement>();
  let trackedActions: readonly PearlAction[] = [];
  let pendingAuth: Record<string, unknown> | null = null;
  let gradualQueue: PearlAction[] = [];
  let gradualDelayTimer: ReturnType<typeof setTimeout> | null = null;
  let gradualIdleTimer: ReturnType<typeof setTimeout> | null = null;
  let gradualLoadTimer: ReturnType<typeof setTimeout> | null = null;
  let gradualWaitingIframe: HTMLIFrameElement | null = null;
  let gradualWaitingOnLoad: (() => void) | null = null;
  let gradualStartedFirst = false;
  let visibilityListenerAttached = false;

  function clearPendingStepTimers(): void {
    if (gradualDelayTimer != null) {
      clearTimeout(gradualDelayTimer);
      gradualDelayTimer = null;
    }
    if (gradualIdleTimer != null) {
      clearTimeout(gradualIdleTimer);
      gradualIdleTimer = null;
    }
  }

  function clearGradualTimers(): void {
    clearPendingStepTimers();
    if (gradualLoadTimer != null) {
      clearTimeout(gradualLoadTimer);
      gradualLoadTimer = null;
    }
    if (gradualWaitingIframe && gradualWaitingOnLoad) {
      gradualWaitingIframe.removeEventListener('load', gradualWaitingOnLoad);
    }
    gradualWaitingIframe = null;
    gradualWaitingOnLoad = null;
  }

  function detachVisibilityListener(): void {
    if (!visibilityListenerAttached) return;
    visibilityListenerAttached = false;
    doc.removeEventListener?.('visibilitychange', onVisibilityChange);
  }

  function stopGradual(): void {
    clearGradualTimers();
    detachVisibilityListener();
    gradualQueue = [];
    gradualStartedFirst = false;
  }

  function nextGradualAction(): PearlAction | null {
    while (gradualQueue.length > 0) {
      const candidate = gradualQueue[0];
      const iframe = byAction.get(candidate);
      if (
        iframe &&
        iframe.hasAttribute(PENDING_SRC_ATTR) &&
        !iframe.hasAttribute(LOAD_ON_OPEN_ATTR)
      ) {
        return candidate;
      }
      gradualQueue.shift();
    }
    return null;
  }

  function scheduleNextGradualStep(delayMs: number): void {
    clearGradualTimers();
    if (!nextGradualAction()) {
      detachVisibilityListener();
      return;
    }
    if (!isDocVisible(doc)) return;

    gradualDelayTimer = setTimeout(() => {
      gradualDelayTimer = null;
      if (!isDocVisible(doc)) return;
      gradualIdleTimer = setTimeout(() => {
        gradualIdleTimer = null;
        runGradualStep();
      }, DEFAULT_GRADUAL_IDLE_FALLBACK_MS);
    }, delayMs);
  }

  function runGradualStep(): void {
    if (!isDocVisible(doc)) return;
    const action = nextGradualAction();
    if (!action) {
      detachVisibilityListener();
      return;
    }
    gradualQueue.shift();
    gradualStartedFirst = true;
    const iframe = byAction.get(action);
    if (!iframe || !iframe.hasAttribute(PENDING_SRC_ATTR)) {
      scheduleNextGradualStep(
        gradualOpts?.stepDelayMs ?? DEFAULT_GRADUAL_STEP_DELAY_MS,
      );
      return;
    }

    const stepDelayMs =
      gradualOpts?.stepDelayMs ?? DEFAULT_GRADUAL_STEP_DELAY_MS;
    const loadTimeoutMs =
      gradualOpts?.loadTimeoutMs ?? DEFAULT_GRADUAL_LOAD_TIMEOUT_MS;

    let settled = false;
    const onSettled = () => {
      if (settled) return;
      settled = true;
      if (gradualLoadTimer != null) {
        clearTimeout(gradualLoadTimer);
        gradualLoadTimer = null;
      }
      if (gradualWaitingIframe === iframe) {
        iframe.removeEventListener('load', onSettled);
        gradualWaitingIframe = null;
        gradualWaitingOnLoad = null;
      }
      scheduleNextGradualStep(stepDelayMs);
    };

    gradualWaitingIframe = iframe;
    gradualWaitingOnLoad = onSettled;
    iframe.addEventListener('load', onSettled);
    gradualLoadTimer = setTimeout(onSettled, loadTimeoutMs);

    ensureNavigated(iframe);
  }

  function onVisibilityChange(): void {
    if (!gradualOpts) return;
    if (!isDocVisible(doc)) {
      clearPendingStepTimers();
      return;
    }
    if (
      gradualDelayTimer == null &&
      gradualIdleTimer == null &&
      gradualLoadTimer == null &&
      gradualWaitingIframe == null &&
      nextGradualAction()
    ) {
      const delay = gradualStartedFirst
        ? (gradualOpts.stepDelayMs ?? DEFAULT_GRADUAL_STEP_DELAY_MS)
        : (gradualOpts.initialDelayMs ?? DEFAULT_GRADUAL_INITIAL_DELAY_MS);
      scheduleNextGradualStep(delay);
    }
  }

  function startGradualQueue(actions: readonly PearlAction[]): void {
    stopGradual();
    if (!gradualOpts) return;
    const priority = gradualOpts.priorityOrder ?? DEFAULT_GRADUAL_PRIORITY;
    const allowed = new Set(
      actions.filter(a => {
        if (a === VOICE_ACTION) return false;
        const iframe = byAction.get(a);
        return (
          !!iframe &&
          iframe.hasAttribute(PENDING_SRC_ATTR) &&
          !iframe.hasAttribute(LOAD_ON_OPEN_ATTR)
        );
      }),
    );
    const ordered: PearlAction[] = [];
    for (const p of priority) {
      if (allowed.has(p)) {
        ordered.push(p);
        allowed.delete(p);
      }
    }
    for (const rest of allowed) {
      ordered.push(rest);
    }
    gradualQueue = ordered;
    if (gradualQueue.length === 0) return;

    if (typeof doc.addEventListener === 'function') {
      doc.addEventListener('visibilitychange', onVisibilityChange);
      visibilityListenerAttached = true;
    }
    scheduleNextGradualStep(
      gradualOpts.initialDelayMs ?? DEFAULT_GRADUAL_INITIAL_DELAY_MS,
    );
  }

  function clearDomIf(): void {
    stopGradual();
    for (const [, iframe] of byAction) {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }
    byAction.clear();
  }

  function iframeEffectiveUrl(iframe: HTMLIFrameElement): string {
    return iframe.getAttribute(PENDING_SRC_ATTR) || iframe.src || '';
  }

  function navigateIframe(iframe: HTMLIFrameElement, url: string): void {
    iframe.removeAttribute(PENDING_SRC_ATTR);
    if (!urlsEqual(iframe.src || '', url)) {
      iframe.removeAttribute(PRELOAD_LOADED_ATTR);
      iframe.src = url;
    }
  }

  function ensureNavigated(iframe: HTMLIFrameElement): void {
    const pending = iframe.getAttribute(PENDING_SRC_ATTR);
    if (pending) navigateIframe(iframe, pending);
  }

  function flushAuthToIframe(iframe: HTMLIFrameElement): void {
    if (!pendingAuth) return;
    if (!isIframeReady(iframe)) return;
    sendAuthToIframe(iframe, pendingAuth);
  }

  function create(
    actions: readonly PearlAction[],
    context: BuildUrlContext,
  ): void {
    clearDomIf();
    trackedActions = [...actions];

    for (const action of trackedActions) {
      const url = resolveUrl(action, context);
      if (!url) continue;
      if (action === 'tooth') logToothNavigate(url, context);

      const isVoice = action === VOICE_ACTION;
      const iframe = doc.createElement('iframe');
      iframe.setAttribute(PRELOAD_ATTR, action);
      iframe.setAttribute(
        'sandbox',
        isVoice ? VOICE_PANEL_SANDBOX : INLINE_PANEL_SANDBOX,
      );
      if (isVoice)
        iframe.setAttribute(
          'allow',
          'microphone; clipboard-write; screen-wake-lock',
        );
      iframe.setAttribute('aria-label', `Pearl ${action}`);
      const loadOnOpen = needsOwnLogin(url) || action === 'patient-report';
      if (loadOnOpen) iframe.setAttribute(LOAD_ON_OPEN_ATTR, '');
      if (deferNavigation || loadOnOpen || gradualOpts) {
        iframe.setAttribute(PENDING_SRC_ATTR, url);
      } else {
        iframe.src = url;
      }
      iframe.style.display = 'none';
      iframe.addEventListener('load', () => {
        iframe.setAttribute(PRELOAD_LOADED_ATTR, '1');
        flushAuthToIframe(iframe);
      });
      panelBody.appendChild(iframe);
      byAction.set(action, iframe);
    }
    if (gradualOpts) {
      startGradualQueue(trackedActions);
    }
  }

  function getIframe(action: PearlAction): HTMLIFrameElement | null {
    return byAction.get(action) ?? null;
  }

  function claimForOpen(url: string): HTMLIFrameElement | null {
    const target = normalizeUrl(url);
    for (const iframe of byAction.values()) {
      if (urlsEqual(iframeEffectiveUrl(iframe), target)) {
        ensureNavigated(iframe);
        return iframe;
      }
    }
    return null;
  }

  function refreshAction(action: PearlAction, context: BuildUrlContext): void {
    const iframe = byAction.get(action);
    if (!iframe) return;
    const nextUrl = resolveUrl(action, context);
    if (!nextUrl) return;
    if (action === 'tooth') logToothNavigate(nextUrl, context);
    if (iframe.hasAttribute(PENDING_SRC_ATTR)) {
      iframe.setAttribute(PENDING_SRC_ATTR, nextUrl);
      return;
    }
    navigateIframe(iframe, nextUrl);
  }

  function refresh(context: BuildUrlContext): void {
    for (const action of trackedActions) {
      refreshAction(action, context);
    }
  }

  function sendAuthToAll(payload: Record<string, unknown>): void {
    pendingAuth = payload;
    for (const [, iframe] of byAction) {
      // Skip deferred iframes that have never navigated — no src origin yet.
      if (!getIframeOrigin(iframe)) continue;
      flushAuthToIframe(iframe);
    }
  }

  function destroy(): void {
    clearDomIf();
    trackedActions = [];
    pendingAuth = null;
  }

  return {
    create,
    getIframe,
    claimForOpen,
    refresh,
    refreshAction,
    destroy,
    sendAuthToAll,
  };
}
